/**
 * Einen übernommenen Import zurücknehmen, mit Vorschau.
 *
 * Eine Löschung, deren Umfang man erst hinterher sieht, ist keine
 * Entscheidung. Deshalb steht vorher da, was verschwindet — Zeilen, Kassen,
 * auch schon geklärte — und was an welchen Tagen passiert. Der Knopf braucht
 * einen zweiten Klick.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { shortDate } from "../lib/format";
import { ErrorBox, Spinner } from "./ui";

interface Vorschau {
  filename: string | null;
  zeilen: { stunden: number; kassenabschluesse: number; terminalReports: number };
  /** Korrekturen, die dieser Import an Zeilen älterer Importe gesetzt hat. */
  korrekturen: number;
  kassen: number;
  davonGeklaert: number;
  blockiert: string[];
  wiederOffen: string[];
  neuZuRechnen: string[];
  ohneWirkung: string[];
}

function Tage({ tage }: { tage: string[] }) {
  // Ab fünf Tagen wird die Liste unlesbar; dann Spanne und Anzahl.
  if (tage.length > 4) {
    return (
      <>
        {shortDate(tage[0]!)}–{shortDate(tage[tage.length - 1]!)} ({tage.length} Tage)
      </>
    );
  }
  return (
    <>
      {tage.map((d, i) => (
        <span key={d}>
          {i > 0 ? ", " : ""}
          <Link to={`/betriebstage/${d}`} className="underline">
            {shortDate(d)}
          </Link>
        </span>
      ))}
    </>
  );
}

export function ImportRuecknahme({ batchId, onDone }: { batchId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [grund, setGrund] = useState("");
  const [bestaetigen, setBestaetigen] = useState(false);
  const vorschau = useQuery({
    queryKey: ["import-ruecknahme", batchId],
    queryFn: () => api.get<Vorschau>(`/imports/${batchId}/ruecknahme`),
  });
  const zuruecknehmen = useMutation({
    mutationFn: () => api.post(`/imports/${batchId}/revert`, grund.trim() ? { reason: grund.trim() } : {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onDone();
    },
  });

  if (vorschau.isLoading) return <Spinner />;
  if (vorschau.error) return <ErrorBox error={vorschau.error} />;
  const v = vorschau.data;
  if (!v) return null;

  const { stunden, kassenabschluesse, terminalReports } = v.zeilen;
  const teile = [
    stunden ? `${stunden} Stundenzeilen` : null,
    kassenabschluesse ? `${kassenabschluesse} Kassenabschlüsse` : null,
    terminalReports ? `${terminalReports} Terminal-Belege` : null,
  ].filter(Boolean);
  const korrekturText =
    v.korrekturen > 0
      ? ` ${v.korrekturen} ${v.korrekturen === 1 ? "Stundenkorrektur" : "Stundenkorrekturen"} aus diesem Import ${v.korrekturen === 1 ? "wird" : "werden"} zurückgenommen; es gilt wieder der vorherige Wert.`
      : "";
  const grundPflicht = v.wiederOffen.length > 0;

  return (
    <div className="space-y-2 py-2 text-sm">
      {v.blockiert.length > 0 ? (
        <p className="text-alert">
          Geht nicht: Die Datei steckt in einer schon gesperrten Auszahlung (<Tage tage={v.blockiert} />). Dafür
          müsste die Administration diese Tage erst entsperren.
        </p>
      ) : (
        <>
          <p>
            Entfernt {teile.length > 0 ? teile.join(", ") : "keine Zeilen"}
            {v.kassen > 0 ? `, dazu ${v.kassen} Kassen, die daraus entstanden sind` : ""}.
            {korrekturText}
            {v.davonGeklaert > 0 ? (
              <strong className="text-warn">
                {" "}
                {v.davonGeklaert} davon {v.davonGeklaert === 1 ? "ist" : "sind"} schon geklärt; diese
                Entscheidungen gehen verloren.
              </strong>
            ) : null}
          </p>
          <ul className="space-y-1 text-xs">
            {v.wiederOffen.length > 0 ? (
              <li className="text-warn">
                Wieder offen, müssen neu freigegeben werden: <Tage tage={v.wiederOffen} />
              </li>
            ) : null}
            {v.neuZuRechnen.length > 0 ? (
              <li className="text-muted">
                Werden neu gerechnet (oder entfallen, wenn nichts mehr da ist): <Tage tage={v.neuZuRechnen} />
              </li>
            ) : null}
            {v.ohneWirkung.length > 0 ? (
              <li className="text-muted">
                Ausgezahlt, ändert sich nichts: <Tage tage={v.ohneWirkung} />
              </li>
            ) : null}
          </ul>
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="label mb-1 block">Grund{grundPflicht ? "" : " (freiwillig)"}</span>
              <input
                className="field w-80"
                placeholder="z. B. falsche Datei gewählt"
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
              />
            </label>
            {bestaetigen ? (
              <>
                <button
                  className="btn-danger"
                  disabled={zuruecknehmen.isPending || (grundPflicht && grund.trim().length < 3)}
                  onClick={() => zuruecknehmen.mutate()}
                >
                  {zuruecknehmen.isPending ? "Nimmt zurück …" : "Ja, zurücknehmen"}
                </button>
                <button className="btn-ghost" onClick={() => setBestaetigen(false)}>
                  Nein
                </button>
              </>
            ) : (
              <button
                className="btn-outline"
                disabled={grundPflicht && grund.trim().length < 3}
                onClick={() => setBestaetigen(true)}
              >
                Zurücknehmen
              </button>
            )}
            <button className="btn-ghost" onClick={onDone}>
              Abbrechen
            </button>
          </div>
        </>
      )}
      {zuruecknehmen.error ? <ErrorBox error={zuruecknehmen.error} /> : null}
    </div>
  );
}
